<template>
  <v-container class="pa-12">
    <v-row class="Report-section">
      <v-col cols="12" md="8" lg="6" xl="4">
        <h2>{{ $t('analytics.pageTitle') }}</h2>
      </v-col>
    </v-row>
    <div class="Report-section">
      <v-row>
        <v-col cols="8" class="align-self-center">
          <h4>{{ $t('analytics.datepicker.label') }}</h4>
        </v-col>
        <v-col cols="4">
          <v-select
            v-model="dateSelect"
            :placeholder="$t('analytics.datepicker.placeholder')"
            :items="dateSelectItems"
            item-text="displayName"
            item-value="value"
            background-color="secondary lighten-1"
            class="ma-2 mb-5"
            height="55px"
            dense
            flat
            hide-details
            light
            solo
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="3">
          <Datepicker v-if="dateSelect === 'custom'" v-model="startDate" :label="$t('analytics.datepicker.start')" />
          <p v-else class="datepickerSeparator">{{ selectedRangeToDate.start }}</p>
        </v-col>
        <v-col cols="1">
          <p class="datepickerSeparator">{{ $t('analytics.datepicker.to') }}</p>
        </v-col>
        <v-col cols="3">
          <Datepicker v-if="dateSelect === 'custom'" v-model="endDate" :label="$t('analytics.datepicker.end')" />
          <p v-else class="datepickerSeparator">{{ selectedRangeToDate.end }}</p>
        </v-col>
      </v-row>
    </div>
    <div class="Report-section" v-if="!loading">
      <v-row>
        <v-col>
          <h4 class="Report-section_title">{{ $t('analytics.appointments.title') }}</h4>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <div v-if="!loading" class="attendingAppointmentStats">
            <BlockStat
              :label="$t('analytics.appointments.total')"
              :raw="totalAppointments.value"
              :value="totalAppointments.text !== undefined ? totalAppointments.text : '-'"
            />
            <BlockStat
              :label="$t('analytics.appointments.attended')"
              :raw="appointmentStats.attended.raw"
              :value="appointmentStats.attended !== undefined ? appointmentStats.attended.value : '-'"
            />
            <BlockStat
              :label="$t('analytics.appointments.canceled')"
              :raw="appointmentStats.canceled.raw"
              :value="appointmentStats.canceled !== undefined ? appointmentStats.canceled.value : '-'"
            />
            <BlockStat
              :label="$t('analytics.appointments.notShow')"
              :raw="appointmentStats.notShow.raw"
              :value="appointmentStats.notShow !== undefined ? appointmentStats.notShow.value : '-'"
            />
          </div>
        </v-col>
      </v-row>
      <v-row class="pt-14">
        <v-col class="pt-14">
          <LineChart :data="lineChartData.dataChart" :label="lineChartData.labelChart"></LineChart>
        </v-col>
      </v-row>
    </div>
    <div class="d-flex flex-row justify-space-between mb-6">
      <BookingSource v-if="!loading" class="pr-7" :items="bookingSource"></BookingSource>
      <TypeOfServices
        v-if="!loading"
        :title="$t('analytics.services.title')"
        :dataDoughnut="data.appointmentsAggregations.subTotals.service"
        :total="totalAppointments.value"
        :totalText="totalAppointments.text"
      ></TypeOfServices>
    </div>
    <appointmentCreator
      v-if="!loading"
      :title="$t('analytics.appointments.creator.title')"
      :appointmentCreators="data.appointmentsAggregations.subTotals.type"
      :total="totalAppointments.value"
    ></appointmentCreator>
  </v-container>
</template>

<script>
import BlockStat from '@/components/report/BlockStat'
import Datepicker from '@/components/ui/Datepicker'
import TypeOfServices from '@/components/report/TypeOfServices'
import BookingSource from '@/components/report/BookingSource'
import LineChart from '@/components/report/LineChart'
import gql from 'graphql-tag'
import * as CONST from '@/const/shared'
import AppointmentCreator from '@/components/report/AppointmentCreator.vue'
import fnsFormat from 'date-fns/format'
import { division } from '@/services/sorter'

const GET_STATISTICS = gql`
  query($shopUuid: GUID!, $beginDate: DateTime!, $endDate: DateTime!) {
    appointmentsAggregations(shopUuid: $shopUuid, beginDate: $beginDate, endDate: $endDate) {
      total
      subTotals {
        age
        service
        status
        type
        origin
      }
      timeSeries {
        key
        count
      }
    }
  }
`
export default {
  name: 'Report',
  components: {
    BlockStat,
    Datepicker,
    TypeOfServices,
    BookingSource,
    LineChart,
    AppointmentCreator
  },
  data() {
    return {
      data: null,
      dateSelect: 'lastWeek',
      startDate: null,
      endDate: null,
      dateSelectItems: [
        {
          value: 'lastWeek',
          displayName: this.$t('analytics.datepicker.lastWeek')
        },
        {
          value: 'lastMonth',
          displayName: this.$t('analytics.datepicker.lastMonth')
        },
        {
          value: 'lastYear',
          displayName: this.$t('analytics.datepicker.lastYear')
        },
        {
          value: 'custom',
          displayName: this.$t('analytics.datepicker.custom')
        }
      ],
      loading: true
    }
  },
  computed: {
    selectedRange() {
      const today = new Date()
      const start = new Date()

      if (this.dateSelect === 'lastWeek') {
        start.setDate(start.getDate() - 7)
      }

      if (this.dateSelect === 'lastMonth') {
        start.setDate(start.getDate() - 30)
      }

      if (this.dateSelect === 'lastYear') {
        start.setDate(start.getDate() - 365)
      }

      if (this.dateSelect === 'custom') {
        return {
          start: new Date(this.startDate),
          end: new Date(new Date(this.endDate).getTime() + 1000 * 3600 * 24 - 1)
        }
      }

      return {
        start,
        end: today
      }
    },
    selectedRangeToDate() {
      return {
        start: fnsFormat(this.selectedRange.start, 'yyyy-MM-dd'),
        end: fnsFormat(this.selectedRange.end, 'yyyy-MM-dd')
      }
    },
    appointmentStatus() {
      return CONST.appointmentStatus
    },
    totalAppointments() {
      return {
        value: this.data.appointmentsAggregations.total,
        text: this.transformStatWithK(this.data.appointmentsAggregations.total)
      }
    },
    appointmentStats() {
      if (!this.data.appointmentsAggregations) {
        return {}
      }
      return Object.entries(this.data.appointmentsAggregations.subTotals.status).reduce((o, [k, v]) => {
        o[k] = {
          raw: v,
          value: this.transformStatWithK(v)
        }
        return o
      }, {})
    },
    lineChartData() {
      return {
        dataChart: this.data.appointmentsAggregations.timeSeries.map(e => e.count),
        labelChart: this.data.appointmentsAggregations.timeSeries.map(e => e.key)
      }
    },
    bookingSource() {
      let sources = []
      for (const [key, value] of Object.entries(this.data.appointmentsAggregations.subTotals.origin)) {
        sources.push({
          title: this.$t(`analytics.sources.sources.${key}`),
          percent: division(value * 100, this.totalAppointments.value),
          value
        })
      }
      return sources
    }
  },
  apollo: {
    appointmentsAggregations: {
      query: GET_STATISTICS,
      skip() {
        if (!this.selectedRange.start || !this.selectedRange.end) {
          return true
        }
        if (this.selectedRange.end < this.selectedRange.start) {
          return true
        }
        return false
      },
      variables() {
        
        this.loading = true
        return {
          shopUuid: this.$store.getters.currentShop.shopUuid,
          beginDate: fnsFormat(this.selectedRange.start, 'yyyy-MM-dd'),
          endDate: this.selectedRange.end.toISOString()
        }
      },
      result({ data }) {
        console.log("plop")
        if (data === null || data === undefined) return
        this.data = data
        this.loading = false
      }
    }
  },
  methods: {
    transformStatWithK(value) {
      return value > 1000 ? (value / 1000).toFixed(0) + 'K' : value
    }
  }
}
</script>
<style lang="scss" scoped>
.Report-section {
  background-color: #fff;
  padding: 20px;
  margin-bottom: 25px;
  border-radius: 3px;
  margin-right: 0px;
  margin-left: 0px;
}
.datepickerText {
  font-size: 18px;
  padding-top: 15px;
}
.datepickerSeparator {
  text-align: center;
  font-weight: bold;
  font-size: 18px;
  padding-top: 15px;
}

.attendingAppointmentStats {
  margin: auto;
  width: 60%;
  display: flex;
  justify-content: space-around;
}

.Report-section_title {
  font-size: 18px;
  font-weight: bold;
}
</style>
